import React from 'react';
import classnames from 'classnames';
import { Tooltip, Tag as ATag } from 'antd';
import type { TagProps as AntTagProps, TagSemanticAllType } from 'antd/es/tag';

import cn from './styles.module.scss';

export type TagType = {
  id: number;
  name: string;
  emoji?: string | null;
  description?: string | null;
  editable?: boolean;
};

export type TagProps = Omit<AntTagProps, 'onClose' | 'onClick' | 'children' | 'classNames'> & {
  tag: TagType;
  maxLength?: number;
  active?: boolean;
  onClose?: (tagId: number) => void;
  onClick?: (tag: TagType) => void;
  classNames?: TagSemanticAllType['classNames'];
};

export const Tag = React.forwardRef<HTMLSpanElement, TagProps>((
  {
    tag,
    maxLength,
    active,
    onClose,
    onClick,
    classNames: classNamesProp,
    ...props
  },
  ref,
) => {
  const text = tag.name;
  const isLongTag = maxLength != null && text.length > maxLength;

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    onClose?.(tag.id);
  };

  const handleClick = () => {
    onClick?.(tag);
  };

  const tagElement = (
    <ATag
      ref={ref}
      {...props}
      classNames={{
        ...classNamesProp,
        root: classnames(classNamesProp?.root, { [cn.active]: active }),
      }}
      onClose={onClose ? handleClose : undefined}
      onClick={onClick ? handleClick : undefined}
    >
      {tag.emoji && <span className={cn.emoji}>{tag.emoji}</span>}
      <span>{isLongTag ? `${text.slice(0, maxLength)}...` : text}</span>
    </ATag>
  );

  if (tag.description || isLongTag) {
    const title = tag.description ? `${text}: ${tag.description}` : text;
    return <Tooltip title={title} key={text}>{tagElement}</Tooltip>;
  }

  return tagElement;
});

Tag.displayName = 'Tag';

export default Tag;
